import { MetaText } from "@/components/plan-audit/AuditPrimitives";
import type { RunSection } from "@/hooks/use-plan-audit";
import type { GuideBlockLabel } from "@/lib/enums";

/**
 * What one scored section actually says: what we found, what the Guide asked
 * for, and what to write instead.
 *
 * The three-part structure is the product. A score on its own is an opinion; a
 * score next to the standard it was measured against, with a proposed rewrite,
 * is something an author can act on — which is the difference the source plan's
 * own audit flagged as missing (GAP-01) from its scoring epics.
 */

/**
 * Guide blocks are tinted by what they are FOR, not decoratively: a warning
 * reads red, an example reads green, the rest stay neutral. Returns token names
 * so a re-brand carries.
 */
function blockTone(label: GuideBlockLabel): { accent: string; surface: string; border: string } {
  switch (label) {
    case "WATCH FOR":
      return {
        accent: "var(--danger)",
        surface: "color-mix(in srgb, var(--danger-bg) 55%, white)",
        border: "color-mix(in srgb, var(--danger) 22%, white)",
      };
    case "EXAMPLE":
      return {
        accent: "var(--success)",
        surface: "color-mix(in srgb, var(--success-bg) 55%, white)",
        border: "color-mix(in srgb, var(--success) 22%, white)",
      };
    case "HOW TO DO IT WELL":
      return { accent: "var(--success)", surface: "var(--bg-1)", border: "var(--border-1)" };
    case "WHY IT MATTERS":
      return { accent: "var(--brand-g-blue)", surface: "var(--bg-1)", border: "var(--border-1)" };
    default:
      return { accent: "var(--brand-primary)", surface: "var(--bg-1)", border: "var(--border-1)" };
  }
}

export function SectionDrawer({ section }: { section: RunSection }) {
  if (!section.hasDetail) {
    return (
      <div className="border-t border-[var(--border-1)] px-2 py-4">
        <p className="text-[13px] text-[var(--fg-2)]">
          This run recorded a score for <b>{section.name}</b> but no commentary.
        </p>
        <MetaText>
          That is not the same as finding nothing wrong — the detail simply was not stored for this
          run. The latest run of this plan carries full commentary.
        </MetaText>
      </div>
    );
  }

  return (
    <div className="grid gap-4 border-t border-[var(--border-1)] px-2 py-4 lg:grid-cols-3">
      <FoundColumn section={section} />
      <GuideColumn section={section} />
      <ProposalColumn section={section} />
    </div>
  );
}

function ColumnHeading({ children }: { children: React.ReactNode }) {
  return <div className="pa-eyebrow mb-2">{children}</div>;
}

function FoundColumn({ section }: { section: RunSection }) {
  return (
    <div>
      <ColumnHeading>What we found</ColumnHeading>
      <p className="mb-3 text-[13px] leading-relaxed text-[var(--fg-1)]">{section.verdict}</p>

      {section.strengths.length > 0 && (
        <div
          className="mb-3 rounded-lg border p-3"
          style={{
            background: "color-mix(in srgb, var(--success-bg) 55%, white)",
            borderColor: "color-mix(in srgb, var(--success) 22%, white)",
          }}
        >
          <div className="pa-eyebrow mb-1" style={{ color: "var(--success-fg)" }}>
            What works
          </div>
          <p className="text-xs leading-relaxed text-[var(--fg-2)]">{section.strengths}</p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {section.problems.map((problem) => (
          <div key={problem.title} className="rounded-lg border border-[var(--border-1)] p-3">
            <div className="mb-1.5 flex items-start justify-between gap-2">
              <span className="text-[13px] font-semibold text-[var(--fg-1)]">{problem.title}</span>
              <span
                className="shrink-0 rounded px-1.5 py-0.5 text-[9.5px] font-bold tracking-wider"
                style={{ background: "var(--bg-3)", color: "var(--fg-3)" }}
                title={
                  problem.source === "AGENT"
                    ? "Introduced by the generating agent"
                    : "Introduced by the prompt framing"
                }
              >
                {problem.source}
              </span>
            </div>
            <p className="text-xs leading-relaxed text-[var(--fg-2)]">{problem.body}</p>
            {problem.quote.length > 0 && (
              <blockquote className="pa-quote mt-2">“{problem.quote}”</blockquote>
            )}
            {problem.note.length > 0 && (
              <p className="mt-2 text-[11px] leading-relaxed text-[var(--fg-3)] italic">
                {problem.note}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function GuideColumn({ section }: { section: RunSection }) {
  return (
    <div>
      <ColumnHeading>What the Guide asks for</ColumnHeading>
      <div className="flex flex-col gap-2">
        {section.guideNotes.map((note) => {
          const tone = blockTone(note.label);
          return (
            <div
              key={note.label}
              className="rounded-lg border p-3"
              style={{
                background: tone.surface,
                borderColor: tone.border,
                boxShadow: `inset 3px 0 0 ${tone.accent}`,
              }}
            >
              <div className="pa-eyebrow mb-1">{note.label}</div>
              <p className="text-xs leading-relaxed text-[var(--fg-2)]">{note.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProposalColumn({ section }: { section: RunSection }) {
  const proposal = section.proposal;
  if (proposal === null) {
    return (
      <div>
        <ColumnHeading>Proposed</ColumnHeading>
        <p className="text-[13px] text-[var(--fg-3)]">No rewrite proposed for this section.</p>
      </div>
    );
  }

  return (
    <div>
      <ColumnHeading>Proposed</ColumnHeading>
      <span
        className="mb-2 inline-flex rounded px-2 py-0.5 text-[10px] font-bold tracking-wider"
        style={{ background: "var(--bg-accent)", color: "var(--brand-accent-hover)" }}
      >
        {proposal.tag}
      </span>
      <p className="text-[13px] leading-relaxed text-[var(--fg-1)]">{proposal.body}</p>

      {proposal.openQuestions.length > 0 && (
        <div className="mt-3">
          <div className="pa-eyebrow mb-2">
            Open question{proposal.openQuestions.length === 1 ? "" : "s"}
          </div>
          <div className="flex flex-col gap-2">
            {proposal.openQuestions.map((question) => (
              <div
                key={question.question}
                className="rounded-lg border p-3"
                style={{
                  background: "color-mix(in srgb, var(--warning-bg) 55%, white)",
                  borderColor: "color-mix(in srgb, var(--warning) 25%, white)",
                }}
              >
                <p className="text-[11px] leading-relaxed text-[var(--fg-2)]">{question.lead}</p>
                {/* Phrased so it can be asked as-is — that is the point of storing
                    the question rather than just the gap. */}
                <p className="mt-1.5 text-xs font-semibold text-[var(--fg-1)]">
                  “{question.question}”
                </p>
                <MetaText>Owner: {question.owner}</MetaText>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
